import { Card } from '@/components/Card';
import { FileIcon, TrashIcon } from '@/components/Icons';
import { queryClient } from '@/constants';
import { DownloadButton } from '@/modules/Files/components/DownloadButton';
import { FileModel } from '@/modules/Infrastructure/domain';
import {
  useAddDocument,
  useDeleteDocument,
} from '@/modules/Infrastructure/queries';
import { InfrastructureCostKey } from '@/modules/Infrastructure/queries/types';
import { t } from 'i18next';
import { ChangeEvent } from 'react';
import { toast } from 'react-toastify';

interface InfrastructureCostFileCardProps {
  title: string;
  files?: Array<FileModel>;
  isLoading?: boolean;
  id?: number;
}

export const InfrastructureCostFileCard = ({
  title,
  files,
  isLoading,
  id,
}: InfrastructureCostFileCardProps) => {
  const { mutate: addDocument } = useAddDocument();
  const { mutate: deleteDocument } = useDeleteDocument();

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      handleAddDocument(selectedFile);
    }
  };

  const handleAddDocument = async (file: File) => {
    if (!id) {
      return;
    }

    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('file', file);

    addDocument(formData, {
      onError: () => {
        toast.error(t('error_adding_document'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(InfrastructureCostKey.BY_ID(id));
        toast.success(t('success_adding_document'));
      },
    });
  };

  const handleDeleteDocument = async (
    fileId: number,
    infrastructureCostFileId: string,
  ) => {
    if (!id) {
      return;
    }
    const convertedData = {
      fileId: Number(fileId),
      infrastructureCostFileId: Number(infrastructureCostFileId),
    };
    deleteDocument(convertedData, {
      onError: () => {
        toast.error(t('error_deleting_document'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(InfrastructureCostKey.BY_ID(id));
        toast.success(t('success_deleting_document'));
      },
    });
  };

  return (
    <Card
      title={title}
      footer={
        <div className="pointer flex cursor-pointer flex-row gap-1 text-shinyBlue">
          +
          <label
            htmlFor="file-upload"
            className="cursor-pointer text-xs font-black underline"
          >
            {t('add_document')}
          </label>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      }
      isLoading={isLoading}
    >
      <div data-testid="files-list" className="flex flex-col gap-2">
        {files?.map((file) => (
          <span key={file.id} className="items flex flex-row gap-2">
            <FileIcon />
            <DownloadButton id={file.id} name={file.name}>
              {file.name}
            </DownloadButton>
            <TrashIcon
              className="cursor-pointer text-rinseGray"
              onClick={() =>
                handleDeleteDocument(file.id, file.infrastructureCostFileId)
              }
            />
          </span>
        ))}
      </div>
    </Card>
  );
};
